"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Check, Copy, Gift, X } from "lucide-react"

import { Button, ButtonLink } from "@/components/ui/button"
import { useCart } from "@/features/cart/hooks/use-cart"
import { LuckyWheel } from "@/features/seasonal/components/lucky-wheel"
import { landingAngle, type SpinResult, type StorefrontDraw } from "@/features/seasonal/lucky-draw"
import type { ThemeId } from "@/features/seasonal/themes"
import { ApiFetchError, apiFetch } from "@/lib/api-fetch"
import { cn } from "@/lib/utils"

type Status = { draw: StorefrontDraw | null }
type Phase = "idle" | "loading" | "ready" | "spinning" | "done" | "error"

// The wheel opens by itself once a visit, this long after the page started loading.
const AUTO_OPEN_MS = 5500
const OPENED_KEY = "skelmet:draw-opened"
let openedHere = false

function opened() {
  if (openedHere) return true
  try {
    return sessionStorage.getItem(OPENED_KEY) === "1"
  } catch {
    return false
  }
}

function rememberOpened() {
  openedHere = true
  try {
    sessionStorage.setItem(OPENED_KEY, "1")
  } catch {
    // No storage (a private window): the flag above still covers this page.
  }
}

/** The draw dressed for a look: its lettering, colours and the mark in its corner. */
type DrawLook = {
  tab: string
  icon: string
  tabText: string
  panel: string
  corner: React.ReactNode
  title: string
  subtitle: string
  won: string
  lost: string
  /** Buttons in the look's lettering. */
  button: string
  spin: string
}

const LOOKS: Record<ThemeId, DrawLook> = {
  halloween: {
    tab: "from-blaze text-bone to-[#7b2cbf]",
    icon: "🎃",
    tabText: "font-spooky text-[15px]",
    panel: "border-[#7b2cbf]/50 bg-[radial-gradient(circle_at_30%_20%,#3a1660,#0d0712_70%)]",
    corner: <span aria-hidden className="halloween-web top-0 left-0 opacity-25" />,
    title:
      "font-spooky text-[34px] drop-shadow-[0_0_18px_rgb(255_106_31_/_0.55)] min-[360px]:text-[40px] sm:text-[46px]",
    subtitle: "bg-blaze text-void font-spooky -rotate-2 rounded-sm px-4 py-1 text-[18px]",
    won: "font-spooky text-acid",
    lost: "font-spooky text-[#b48cff]",
    button: "font-spooky text-[18px] tracking-[0.04em] normal-case",
    spin: "font-spooky text-[20px] tracking-[0.04em] normal-case",
  },
  diwali: {
    tab: "from-[#ff9f1c] text-bone to-[#c2185b]",
    icon: "🪔",
    tabText: "font-festive text-[14px]",
    panel: "border-[#ffc94a]/40 bg-[radial-gradient(circle_at_30%_20%,#5a1435,#0d0610_70%)]",
    corner: <span aria-hidden className="diwali-corner" />,
    title: "font-festive diwali-gold text-[30px] min-[360px]:text-[36px] sm:text-[42px]",
    subtitle: "bg-[#ffc94a] text-[#3a0a1e] font-festive -rotate-2 rounded-sm px-4 py-1 text-[16px]",
    won: "font-festive diwali-gold",
    lost: "font-festive text-[#ff8fc0]",
    button: "font-festive text-[16px] tracking-[0.03em] normal-case",
    spin: "font-festive text-[18px] tracking-[0.03em] normal-case",
  },
}

function Result({
  result,
  look,
  onAgain,
  onDone,
}: {
  result: SpinResult
  look: DrawLook | null
  onAgain: () => void
  onDone: () => void
}) {
  const setCoupon = useCart((s) => s.setCoupon)
  const gift = useCart((s) => s.gift)
  const setGift = useCart((s) => s.setGift)
  const couponCode = useCart((s) => s.couponCode)
  const [copied, setCopied] = React.useState(false)
  const won = result.kind !== "nothing"

  const copy = async () => {
    if (!result.couponCode) return
    try {
      await navigator.clipboard.writeText(result.couponCode)
      setCopied(true)
    } catch {
      // No clipboard (an insecure page, an old browser): the code is on screen to type.
    }
  }

  return (
    <div role="status" className="flex flex-col items-center gap-3 text-center">
      <p
        className={cn(
          "text-[44px] leading-none",
          look
            ? won
              ? look.won
              : look.lost
            : cn("font-display uppercase", won ? "text-acid" : "text-[#b48cff]"),
        )}
      >
        {won ? `${result.label}!` : `${result.label}…`}
      </p>
      <p className="text-bone text-[16px] font-semibold">
        {result.kind === "coupon"
          ? `${result.detail || "A discount"} on your order`
          : result.kind === "gift"
            ? `Free ${result.gift} with your next order`
            : result.detail || "Better luck next time"}
      </p>

      {result.kind === "coupon" && result.couponCode ? (
        <div className="flex w-full flex-col gap-2.5">
          <button
            type="button"
            onClick={copy}
            className="border-acid/40 text-acid hover:border-acid flex h-12 items-center justify-center gap-3 rounded-full border border-dashed font-mono text-[16px] font-bold tracking-[0.14em] transition-colors"
          >
            {result.couponCode}
            {copied ? (
              <Check className="size-4" strokeWidth={2.6} />
            ) : (
              <Copy className="size-4" strokeWidth={2} />
            )}
            <span className="sr-only">{copied ? "Copied" : "Copy the code"}</span>
          </button>
          <Button
            type="button"
            variant="primary"
            size="md"
            full
            disabled={couponCode === result.couponCode}
            onClick={() => setCoupon(result.couponCode)}
          >
            {couponCode === result.couponCode ? (
              <>
                <Check className="size-4" strokeWidth={2.6} />
                Applied to your cart
              </>
            ) : (
              "Apply to my cart"
            )}
          </Button>
        </div>
      ) : null}

      {result.kind === "gift" && result.claimCode && result.gift ? (
        <Button
          type="button"
          variant="accent"
          size="md"
          full
          disabled={gift?.code === result.claimCode}
          onClick={() => setGift({ code: result.claimCode!, name: result.gift! })}
        >
          {gift?.code === result.claimCode ? (
            <>
              <Check className="size-4" strokeWidth={2.6} />
              Added to your cart
            </>
          ) : (
            <>
              <Gift className="size-4" strokeWidth={2} />
              Add the gift to my cart
            </>
          )}
        </Button>
      ) : null}

      <div className="grid w-full grid-cols-2 gap-2.5">
        <Button
          type="button"
          variant={won ? "ghost" : "primary"}
          size="md"
          full
          onClick={onAgain}
          className={look?.button}
        >
          Spin again
        </Button>
        <ButtonLink href="/products" variant="ghost" size="md" full onClick={onDone}>
          Shop mounts
        </ButtonLink>
      </div>
    </div>
  )
}

/**
 * The Spin to win tab and its wheel. Shown to everyone, or for a staff-only draw only once the
 * server says this viewer is staff. The result is the server's: the wheel only turns to it.
 */
export function LuckyDraw({ draw, season }: { draw: StorefrontDraw; season: ThemeId | null }) {
  const look = season ? LOOKS[season] : null
  const pathname = usePathname()
  const [status, setStatus] = React.useState<Status | null>(null)
  const [staffChecked, setStaffChecked] = React.useState(draw.offer === "everyone")
  const [open, setOpen] = React.useState(false)
  const [phase, setPhase] = React.useState<Phase>("idle")
  const [error, setError] = React.useState<string | null>(null)
  const [rotation, setRotation] = React.useState(0)
  const [result, setResult] = React.useState<SpinResult | null>(null)
  const tab = React.useRef<HTMLButtonElement>(null)
  const panel = React.useRef<HTMLDivElement>(null)
  const settleTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const wheel = status?.draw ?? draw

  const load = async () => {
    const next = await apiFetch<Status>("/api/public/lucky-draw")
    setStatus(next)
    return next
  }

  // A staff-only draw: ask whether this viewer may see it.
  React.useEffect(() => {
    if (draw.offer !== "staff") return
    let current = true
    apiFetch<Status>("/api/public/lucky-draw")
      .then((next) => {
        if (current) setStatus(next)
      })
      .catch(() => null)
      .finally(() => {
        if (current) setStaffChecked(true)
      })
    return () => {
      current = false
    }
  }, [draw.offer])

  const show = async () => {
    rememberOpened()
    setOpen(true)
    setError(null)
    if (status) {
      // The last result stays on screen until they spin again.
      setPhase(result ? "done" : "ready")
      return
    }
    setPhase("loading")
    try {
      const next = await load()
      if (!next.draw) {
        setError("The draw has just closed. Thanks for stopping by!")
        setPhase("error")
        return
      }
      setPhase("ready")
    } catch {
      setError("The wheel couldn't load. Check your connection and try again.")
      setPhase("error")
    }
  }

  const close = React.useCallback(() => {
    if (phase === "spinning") return
    setOpen(false)
    tab.current?.focus()
  }, [phase])

  const settle = React.useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current)
    setPhase((p) => (p === "spinning" ? "done" : p))
  }, [])

  const spin = async () => {
    setError(null)
    setPhase("spinning")
    try {
      const won = await apiFetch<SpinResult>("/api/public/lucky-draw/spin", { method: "POST" })
      setResult(won)
      const index = wheel.segments.findIndex((s) => s.id === won.prizeId)
      // A segment changed in the console since the wheel loaded: show the result unturned.
      if (index < 0) {
        setPhase("done")
        return
      }
      setRotation((r) =>
        landingAngle(index, wheel.segments.length, r, { at: 0.2 + Math.random() * 0.6 }),
      )
      // transitionend can be skipped (a hidden tab); the result shows regardless.
      settleTimer.current = setTimeout(settle, 6000)
    } catch (err) {
      setError(
        err instanceof ApiFetchError
          ? err.message
          : "The wheel couldn't spin. Try again in a moment.",
      )
      setPhase("ready")
    }
  }

  // Modal: Escape closes, focus stays inside, the page behind holds still.
  React.useEffect(() => {
    if (!open) return
    const el = panel.current
    el?.querySelector<HTMLElement>("button, a[href]")?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
      if (e.key !== "Tab" || !el) return
      const items = Array.from(el.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"))
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [open, close])

  React.useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current)
    },
    [],
  )

  const hidden =
    pathname.startsWith("/checkout") || !staffChecked || (draw.offer === "staff" && !status?.draw)

  // Never over the splash or another dialog, while a field is being typed in, or in a hidden tab.
  const autoOpen = React.useEffectEvent(() => void show())
  React.useEffect(() => {
    if (hidden || opened()) return
    let timer: ReturnType<typeof setTimeout>
    const attempt = () => {
      if (opened()) return
      const busy =
        document.hidden ||
        document.querySelector("[data-splash]") ||
        // The menu and the cart stay in the page while closed, inert.
        [...document.querySelectorAll("[aria-modal='true']")].some((d) => !d.closest("[inert]")) ||
        document.activeElement?.matches("input, textarea, select")
      if (busy) timer = setTimeout(attempt, 1500)
      else autoOpen()
    }
    timer = setTimeout(attempt, Math.max(0, AUTO_OPEN_MS - performance.now()))
    return () => clearTimeout(timer)
  }, [hidden])

  if (hidden && !open) return null

  return (
    <>
      <button
        ref={tab}
        type="button"
        onClick={show}
        aria-haspopup="dialog"
        className={cn(
          "draw-tab fixed top-1/2 right-0 z-[34] flex -translate-y-1/2 flex-col items-center gap-2 rounded-l-xl px-2 py-3.5",
          "from-blaze to-ember text-void bg-gradient-to-b shadow-[0_10px_30px_rgb(255_90_31_/_0.45)]",
          "focus-visible:ring-bone/80 focus-visible:ring-2 focus-visible:outline-none",
          look?.tab,
        )}
      >
        <span aria-hidden className="text-[18px] leading-none">
          {look?.icon ?? "🎁"}
        </span>
        <span
          className={cn(
            "text-[13px] leading-none tracking-[0.08em] [writing-mode:vertical-rl]",
            look ? look.tabText : "font-display uppercase",
          )}
        >
          Spin to win
        </span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close"
            onClick={close}
            className="bg-void/85 absolute inset-0 backdrop-blur-sm"
          />
          {/* Never scrolls. Phones stack title, wheel and buttons in a screen-high card, the wheel
              taking whatever height is left; from tablets the wheel sits left of the rest. */}
          <div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="lucky-draw-title"
            className={cn(
              "rounded-card relative flex h-[min(calc(100dvh-24px),780px)] w-full max-w-[440px] flex-col items-center gap-4 overflow-hidden border p-5 sm:p-7",
              "md:grid md:h-auto md:max-h-[calc(100dvh-48px)] md:max-w-[900px] md:grid-cols-[minmax(0,1fr)_340px] md:grid-rows-[auto_1fr] md:items-center md:justify-items-center md:gap-x-9 md:gap-y-5 md:p-9",
              look ? look.panel : "bg-carbon border-white/[0.1]",
            )}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close the lucky draw"
              disabled={phase === "spinning"}
              className="text-ash hover:text-bone absolute top-3 right-3 z-10 flex size-10 items-center justify-center rounded-full transition-colors disabled:opacity-40"
            >
              <X className="size-5" strokeWidth={2} />
            </button>
            {look?.corner}

            <div className="flex flex-col items-center text-center md:col-start-2 md:row-start-1 md:items-start md:self-end md:text-left">
              <h2
                id="lucky-draw-title"
                className={cn(
                  "text-bone leading-[1.0] text-balance",
                  look ? look.title : "font-display text-[32px] uppercase sm:text-[38px]",
                )}
              >
                {wheel.title}
              </h2>
              {wheel.subtitle ? (
                <p className={cn("mt-3", look ? look.subtitle : "text-ash text-[14px]")}>
                  {wheel.subtitle}
                </p>
              ) : null}
            </div>

            {/* Phones: a box as big as the space left, the wheel the largest square in it. */}
            <div className="[container-type:size] flex min-h-0 w-full flex-1 items-center justify-center md:[container-type:normal] md:col-start-1 md:row-span-2 md:row-start-1 md:w-[min(100%,calc(100dvh-140px),480px)] md:flex-none">
              <div className="w-[min(100cqw,100cqh)] md:w-full">
                <LuckyWheel
                  season={season}
                  segments={wheel.segments}
                  rotation={rotation}
                  spinning={phase === "spinning"}
                  onSettled={settle}
                />
              </div>
            </div>

            <div className="w-full md:col-start-2 md:row-start-2" aria-live="polite">
              {phase === "done" && result ? (
                <Result
                  result={result}
                  look={look}
                  onAgain={() => void spin()}
                  onDone={() => setOpen(false)}
                />
              ) : phase === "error" ? (
                <p className="text-ash text-center text-[14px]">{error}</p>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    full
                    disabled={phase !== "ready"}
                    onClick={spin}
                    className={look?.spin}
                  >
                    {phase === "spinning"
                      ? "Spinning…"
                      : phase === "loading"
                        ? "Loading…"
                        : look
                          ? "Spin to find out"
                          : "Spin the wheel"}
                  </Button>
                  {error ? <p className="text-magenta text-center text-[13px]">{error}</p> : null}
                  <p className="text-dim text-center text-[12px] leading-[1.5]">
                    Spin as often as you like. Discounts apply at checkout;{" "}
                    <Link
                      href="/policies/terms"
                      className="underline underline-offset-2"
                      onClick={close}
                    >
                      terms apply
                    </Link>
                    .
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
